import { useEffect, useState } from "react";

/**
 * The revision info attached to a file version. SERVER SCHEMA GAP: `directoryDataFileItemSchema`
 * declares `revision` as `{ value?: string }` only, but this panel also reads `createdBy` and
 * `createdDate` off it — and `files.versions[].revision` is declared a plain `string` elsewhere
 * (fileOperations.schemas.ts:710). Three declarations, three shapes; this is what the panel reads.
 *
 * `value` is NULLABLE, not merely optional: the collection census recorded on
 * `fileOperations.collection.ts:231-233` counts `value` as null on 61177 versions and a string on 58.
 */
type RevisionInfo = { value?: string | null; createdBy?: string; createdDate?: number };

/**
 * A file version from the `fileVersions` store slice, plus the two members this panel reads that the
 * slice's own element type does not declare (`revision`, `fileVersionNumber`) — a third place where
 * the revision shape is under-declared.
 */
type FileVersionLike = NonNullable<RootState["fileVersions"][string]>[number] & {
  /**
   * NULLABLE, not just optional. The rows come from `/fileOperations/getFileVersions`, whose
   * `$project` forwards `revision: "$versions.revision"` verbatim
   * (fileOperations.mongo.service.ts:464), and the `files` census on
   * `fileOperations.collection.ts:230-233` counts `versions[].revision` as an object on 66126
   * entries and explicitly `null` on 15674. `null` therefore reaches this panel.
   */
  revision?: RevisionInfo | null;
  fileVersionNumber?: number;
};

/** One rendered row: the spread file version, with its revision guaranteed present by the filter. */
type RevisionRow = FileVersionLike & { revision: RevisionInfo };
import { useSelector } from "react-redux";
import { convertDate } from "@Services/luxonHelper";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import { useTranslation } from "@i18n";
import { RootState } from "@Reducers/index";

function RevisionContent({ resourceId }: { resourceId?: string }) {
  const { t } = useTranslation();
  const fileVersions = useSelector((state: RootState) => state.fileVersions[resourceId!]);
  const [revisions, setRevisions] = useState<RevisionRow[]>([]);

  useEffect(() => {
    if (fileVersions && fileVersions.length) {
      const allFileVersionRevisions = fileVersions
        // Truthiness, NOT `!== undefined`: `revision` is stored as `null` on 15674 versions (census
        // above), and the rendering below reads `.value` / `.createdBy` / `.createdDate` off it
        // unconditionally — letting a null row through throws on the first dereference. The
        // predicate is what makes `revision` non-null on the rows.
        .filter((fileVersion: FileVersionLike): fileVersion is RevisionRow => Boolean(fileVersion.revision))
        .map((fileVersion: RevisionRow) => ({
          ...fileVersion,
          revision: fileVersion.revision,
        }));

      setRevisions(allFileVersionRevisions.sort((a: RevisionRow, b: RevisionRow) => b.createdDate! - a.createdDate!));
    }
  }, [fileVersions]);

  const userAvatar = (userId: string) => <UserNameTagComponent id={userId} />;

  return (
    <div className="p-6 rounded-lg shadow-sm">
      {
        <div className="space-y-4">
          {revisions.map((revision: RevisionRow, index: number) => (
            <div key={index} className="mb-4 last:mb-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-semibold">{revision.revision.value}</span>
                <span className="text-bold">V{revision.fileVersionNumber}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <span>{t("Created by")}</span>
                <div className="flex items-center">{userAvatar(revision.revision.createdBy!)}</div>
                <span className="text-sm">{convertDate(Number(revision.revision.createdDate), "dateForText")}</span>
              </div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

export default RevisionContent;
